import React, { useCallback, useEffect } from 'react';
import {
    Button,
    ButtonTheme,
    DropDownBox,
    DropDownBoxComponentSpacing,
    ShowIf,
    TextBox,
} from '@apply/components';
import {
    useDeleteReference,
    useReferencesQuery,
    useSaveReference,
} from '../queries/businessProfileQueries';
import { BusinessReference } from '../types';
import { SummaryCard, SummaryRow } from '../components/SummaryCard';
import { FormModal } from '../components/FormModal';
import { REFERENCE_TYPE_OPTIONS } from '../constants/options';
import { StepErrorBanner } from '../components/StepErrorBanner';
import { StepStatus } from '../components/StepStatus';
import { useActionError } from '../hooks/useActionError';
import { RegisterStep } from '../wizard/stepApi';
import { asInput } from '../components/inputValue';
import { useItemEditor } from '../hooks/useItemEditor';

interface ReferencesStepProps {
    businessProfileId: number | null;
    onRegister: RegisterStep;
}

const INSTRUCTIONS =
    'Please include 1 primary bank for business accounts, 1 supplier, and 2 trades. ' +
    'If possible, provide a second banking/financial reference.';

export const ReferencesStep: React.FC<ReferencesStepProps> = ({ businessProfileId, onRegister }) => {
    const profileId = businessProfileId ?? 0;
    const { data: references, isLoading, isError, error, refetch } = useReferencesQuery(profileId);
    const { mutateAsync: saveReference } = useSaveReference();
    const { mutateAsync: deleteReference } = useDeleteReference(profileId);
    const rowAction = useActionError();

    const existing = references ?? [];

    const makeEmpty = useCallback(
        (): BusinessReference => ({
            id: 0,
            businessProfileId: profileId,
            referenceType: '',
            companyName: '',
            contactName: '',
            phone: '',
            email: '',
            lastEditedUtc: null,
        }),
        [profileId],
    );

    const editor = useItemEditor<BusinessReference>({
        makeEmpty,
        save: (reference) => saveReference({ ...reference, businessProfileId: profileId }),
    });

    // References are saved from the popup, so Next has nothing to persist
    const save = useCallback(async () => undefined, []);

    useEffect(() => {
        onRegister({
            save,
            canAdvance: true, // Not currently enforcing reference type minimums (as described in the Add modal description)
        });
    }, [save, existing.length, onRegister]);

    const update = <K extends keyof BusinessReference>(key: K, value: BusinessReference[K]) =>
        editor.setDraft((prior) => ({ ...prior, [key]: value }));

    const rows: SummaryRow[] = existing.map((reference) => ({
        key: String(reference.id),
        title: reference.contactName || reference.companyName,
        subtitle: reference.referenceType,
        actions: [
            { label: 'Edit Details', onClick: () => editor.openEdit(reference) },
            {
                label: 'Delete Reference',
                destructive: true,
                onClick: () => {
                    void rowAction.run(() => deleteReference(reference.id));
                },
            },
        ],
    }));

    if (isLoading || isError) {
        return (
            <StepStatus
                isLoading={isLoading}
                isError={isError}
                error={error}
                onRetry={() => void refetch()}
            />
        );
    }

    return (
        <div className='bp-step'>
            {rowAction.error != null && (
                <StepErrorBanner
                    error={rowAction.error}
                    onDismiss={rowAction.clear}
                />
            )}
            <ShowIf showIf={existing.length === 0}>
                <p className='bp-step__instructions'>{INSTRUCTIONS}</p>
            </ShowIf>

            <ShowIf showIf={existing.length > 0}>
                <SummaryCard
                    id='bpReferences'
                    heading='References'
                    rows={rows}
                />
            </ShowIf>

            <div style={{ marginTop: 24 }}>
                <Button
                    id='bpAddReferenceButton'
                    dataTestid='bpAddReferenceButton'
                    theme={existing.length === 0 ? ButtonTheme.Primary : ButtonTheme.Secondary}
                    width='100%'
                    maxWidth='346px'
                    handleClick={editor.openAdd}
                >
                    {existing.length === 0 ? 'Add Reference' : 'Add Another Reference'}
                </Button>
            </div>

            <FormModal
                id='bpReferenceModal'
                isOpen={editor.isOpen}
                title={editor.isEditing ? 'Edit Reference' : 'Add Reference'}
                onSave={editor.commit}
                onClose={editor.close}
            >
                <p className='bp-step__instructions'>{INSTRUCTIONS}</p>

                <DropDownBox
                    id='bpReferenceType'
                    value={asInput(editor.draft.referenceType)}
                    onChange={(v) => update('referenceType', v)}
                    label='Reference Type'
                    required={true}
                    dataSource={REFERENCE_TYPE_OPTIONS}
                    valueExpr='value'
                    displayExpr='display'
                    componentSpacing={DropDownBoxComponentSpacing.SmallSpacing}
                />

                <TextBox
                    id='bpReferenceCompany'
                    value={asInput(editor.draft.companyName)}
                    onChange={(v) => update('companyName', v)}
                    label='Company Name'
                    required={true}
                    maxLength={100}
                />

                <TextBox
                    id='bpReferenceContact'
                    value={asInput(editor.draft.contactName)}
                    onChange={(v) => update('contactName', v)}
                    label='Contact Name'
                    maxLength={100}
                />

                <TextBox
                    id='bpReferencePhone'
                    value={asInput(editor.draft.phone)}
                    onChange={(v) => update('phone', v)}
                    label='Phone Number'
                    type='tel'
                    maxLength={20}
                />

                <TextBox
                    id='bpReferenceEmail'
                    value={asInput(editor.draft.email)}
                    onChange={(v) => update('email', v)}
                    label='Email Address'
                    type='email'
                    maxLength={200}
                />
            </FormModal>
        </div>
    );
};
